/**
 * EL RESOLVEDOR SEGURO: DE UN NOMBRE ESCRITO A MANO A UN CÓDIGO SIEX
 * (01-10-2026, plan cultivos SIEX, F1; la regla es la de F5).
 *
 * «100 % seguro» quiere decir, y nada más:
 *   - Nivel A: el texto normalizado es IGUAL al nombre oficial de UN SOLO
 *     cultivo activo («TOMATE», «Calabacín», «col china» si llega el catálogo).
 *   - Nivel B: es igual a un sinónimo SEGURO (`nombres`) de UN SOLO cultivo
 *     curado: traducciones y plurales (Pomodoro, Tomato, Lattuga). D1 de Pablo.
 *   - Todo lo demás (nivel C) NUNCA se empareja solo: erratas («Berengena»),
 *     tipos («Cherry»), cultivo + variedad («Pimiento Palermo») salen como
 *     SUGERENCIAS para que decida una persona; «Varios» no sale ni eso.
 * Si hay dos candidatos, no es seguro: sugerencias.
 *
 * Funciona SOLO con la lista curada (sin BD: la edge, el trigger de pruebas,
 * los tests) y, si se le pasa, con el catálogo entero para los nombres
 * oficiales de los cultivos no curados (1.120 en SIEX). Sin catálogo, «col
 * china» es una sugerencia de Col; con catálogo es 304 COL CHINA por nivel A.
 *
 * Se compara con `claveBusqueda` (cualquier alfabeto), no con `claveLatina`:
 * los nombres por defecto de la app en chino, árabe, bambara y mandinga
 * también tienen que resolver.
 *
 * PURO: viaja a Deno en _shared/cultivosCurados.generated.ts.
 */
import { claveBusqueda } from "./clave";
import { CULTIVOS_CURADOS, cultivoCuradoPorCodigo, type CultivoCurado } from "./curados";

/** Lo mínimo del catálogo que el resolvedor necesita (encaja con los items de `crop_catalog_list()`). */
export interface CultivoOficialMinimo {
  /** Código SIEX. */
  c: number;
  /** Nombre oficial SIEX. */
  o: string;
  /** Fecha de baja en SIEX (ISO), si la tiene. */
  b?: string | null;
}

export type MotivoSugerencia = "errata" | "tipo" | "prefijo";

export interface SugerenciaCultivo {
  code: number;
  motivo: MotivoSugerencia;
  /** Lo que sobra tras el nombre del cultivo («Palermo» de «Pimiento Palermo»), por si es la variedad. */
  varietyHint?: string;
}

export type ResultadoResolver =
  | { code: number; via: "oficial" | "nombre" }
  | { code: null; sugerencias: SugerenciaCultivo[] };

export interface OpcionesResolver {
  /** Hoy en ISO (YYYY-MM-DD): una baja con fecha futura aún no cuenta. Por defecto, hoy. */
  hoy?: string;
}

/** Baja vigente: tiene fecha y ya ha llegado. */
export function estaDeBaja(baja: string | null | undefined, hoy: string): boolean {
  return !!baja && baja.slice(0, 10) <= hoy;
}

function hoyIso(): string {
  return new Date().toISOString().slice(0, 10);
}

type Indice = Map<string, Set<number>>;

function apunta(m: Indice, texto: string, code: number) {
  const k = claveBusqueda(texto);
  if (!k) return;
  let s = m.get(k);
  if (!s) m.set(k, (s = new Set()));
  s.add(code);
}

interface IndicesCurados {
  oficial: Indice;
  nombre: Indice;
  errata: Indice;
  tipo: Indice;
  regfiwebTipo: Map<string, string>;
}

/* La lista curada no cambia en caliente: sus índices se hacen una vez. */
const CURADOS: IndicesCurados = (() => {
  const r: IndicesCurados = { oficial: new Map(), nombre: new Map(), errata: new Map(), tipo: new Map(), regfiwebTipo: new Map() };
  for (const c of CULTIVOS_CURADOS) {
    apunta(r.oficial, c.oficial, c.siex);
    // El nombre visible (Judía, Col) es tan seguro como sus sinónimos: nivel B.
    apunta(r.nombre, c.visible, c.siex);
    for (const n of c.nombres) apunta(r.nombre, n, c.siex);
    for (const e of c.erratas ?? []) apunta(r.errata, e, c.siex);
    for (const t of c.tipos ?? []) apunta(r.tipo, t, c.siex);
    for (const [t, reg] of Object.entries(c.regfiwebTipos ?? {})) {
      const k = claveBusqueda(t);
      if (k) r.regfiwebTipo.set(k, reg);
      apunta(r.tipo, t, c.siex);
    }
  }
  return r;
})();

/* El índice de oficiales del catálogo, por lista (la misma lista llega en cada pulsación). */
const OFICIALES_CATALOGO = new WeakMap<readonly CultivoOficialMinimo[], { hoy: string; activos: Indice; deBaja: Set<number> }>();

function oficialesDe(catalogo: readonly CultivoOficialMinimo[], hoy: string) {
  const hecho = OFICIALES_CATALOGO.get(catalogo);
  if (hecho && hecho.hoy === hoy) return hecho;
  const activos: Indice = new Map();
  const deBaja = new Set<number>();
  for (const it of catalogo) {
    if (estaDeBaja(it.b, hoy)) deBaja.add(it.c);
    else apunta(activos, it.o, it.c);
  }
  const r = { hoy, activos, deBaja };
  OFICIALES_CATALOGO.set(catalogo, r);
  return r;
}

function unico(s: Set<number> | undefined): number | null {
  return s && s.size === 1 ? [...s][0] : null;
}

/** Lo escrito tal cual (espacios colapsados), partido en palabras con su clave: para sacar la pista de variedad. */
function palabras(raw: string): Array<{ texto: string; clave: string }> {
  return raw
    .split(/\s+/)
    .filter(Boolean)
    .map((texto) => ({ texto, clave: claveBusqueda(texto) }))
    .filter((p) => p.clave);
}

/**
 * «Tomate» → {code:197, via:'oficial'}; «Pomodoro» → {code:197, via:'nombre'};
 * «Pimiento Palermo» → sugerencia 152 'prefijo' con varietyHint 'Palermo';
 * «Berengena» → sugerencia 162 'errata'; «Varios» → sin código y sin sugerencias.
 */
export function resolverCultivoSeguro(
  raw: string | null | undefined,
  catalogo?: readonly CultivoOficialMinimo[] | null,
  opciones: OpcionesResolver = {},
): ResultadoResolver {
  const k = claveBusqueda(raw);
  if (!k) return { code: null, sugerencias: [] };
  const hoy = opciones.hoy ?? hoyIso();
  const cat = catalogo && catalogo.length ? oficialesDe(catalogo, hoy) : null;

  // Nivel A: nombre oficial de un único cultivo ACTIVO. Los curados son todos
  // activos (lo vigila el test del fixture); con catálogo, manda el catálogo,
  // que sabe de bajas y de nombres repetidos. Un curado que el catálogo da de
  // baja ya no es activo: ni por A, ni por B, ni como sugerencia.
  const vivo = (code: number) => !cat?.deBaja.has(code);
  const oficiales = new Set<number>([...(CURADOS.oficial.get(k) ?? [])].filter(vivo));
  for (const code of cat?.activos.get(k) ?? []) oficiales.add(code);
  const a = unico(oficiales);
  if (a != null) return { code: a, via: "oficial" };

  // Nivel B: sinónimo seguro de un único cultivo curado (y que no choque con
  // el nombre oficial de OTRO cultivo: entonces no es seguro).
  const nombres = new Set([...(CURADOS.nombre.get(k) ?? [])].filter(vivo));
  if (oficiales.size === 0) {
    const b = unico(nombres);
    if (b != null) return { code: b, via: "nombre" };
  }

  // Nivel C: solo sugerencias, en este orden y sin repetir cultivo.
  const sugerencias: SugerenciaCultivo[] = [];
  const vistos = new Set<number>();
  const sugiere = (code: number, motivo: MotivoSugerencia, varietyHint?: string) => {
    if (vistos.has(code) || !vivo(code)) return;
    vistos.add(code);
    sugerencias.push(varietyHint ? { code, motivo, varietyHint } : { code, motivo });
  };
  /* Empate exacto (dos oficiales activos con el mismo nombre, o un sinónimo de
     dos cultivos): no hay motivo propio en el contrato; va como 'prefijo', que
     es «se parece, decide tú». */
  for (const code of oficiales) sugiere(code, "prefijo");
  for (const code of nombres) sugiere(code, "prefijo");

  const ps = palabras(String(raw ?? ""));
  /* Erratas: entera, sin pista («Berengena»); al principio, lo que sobra («Berengena larga» → «larga»). */
  for (const code of CURADOS.errata.get(k) ?? []) sugiere(code, "errata");
  for (let n = ps.length - 1; n >= 1; n--) {
    const cabeza = ps.slice(0, n).map((p) => p.clave).join(" ");
    const resto = ps.slice(n).map((p) => p.texto).join(" ");
    for (const code of CURADOS.errata.get(cabeza) ?? []) sugiere(code, "errata", resto);
  }
  /* Tipos, enteros o al principio («Tomate Cherry», «Cherry pera»). */
  for (const code of CURADOS.tipo.get(k) ?? []) sugiere(code, "tipo", pistaDeTipo(ps, code));
  for (let n = ps.length - 1; n >= 1; n--) {
    const cabeza = ps.slice(0, n).map((p) => p.clave).join(" ");
    for (const code of CURADOS.tipo.get(cabeza) ?? []) sugiere(code, "tipo", pistaDeTipo(ps, code));
  }

  /* Cultivo + algo más: el nombre más largo que case al principio. */
  for (let n = ps.length - 1; n >= 1; n--) {
    const cabeza = ps.slice(0, n).map((p) => p.clave).join(" ");
    const resto = ps.slice(n).map((p) => p.texto).join(" ");
    const codes = new Set<number>([
      ...(CURADOS.oficial.get(cabeza) ?? []),
      ...(cat?.activos.get(cabeza) ?? []),
      ...(CURADOS.nombre.get(cabeza) ?? []),
    ]);
    for (const code of codes) sugiere(code, "prefijo", resto);
    if (codes.size) break;
  }

  return { code: null, sugerencias };
}

/**
 * La pista de variedad de un tipo: si empieza por el nombre del cultivo
 * («Tomate Cherry»), lo que sigue; si no («California Rojo», «Cherry»), todo.
 */
function pistaDeTipo(ps: Array<{ texto: string; clave: string }>, code: number): string | undefined {
  const c = cultivoCuradoPorCodigo(code);
  if (!c || !ps.length) return undefined;
  const nombres = new Set([c.oficial, c.visible, ...c.nombres].map(claveBusqueda));
  for (let n = ps.length - 1; n >= 1; n--) {
    if (nombres.has(ps.slice(0, n).map((p) => p.clave).join(" "))) {
      return ps.slice(n).map((p) => p.texto).join(" ");
    }
  }
  return ps.map((p) => p.texto).join(" ");
}

/**
 * El nombre del cultivo en el Registro de Fitosanitarios (regfiweb) para lo que
 * escribe alguien: «pepino» → «Pepino», «brocoli» → «Bróculi», «pepinillo» →
 * «Pepinillo» (tipo que el registro separa). null si no es seguro o no se sabe.
 * Es lo único que usa `registryMapa.ts` desde F9 (05-10-2026), que retiró su
 * tabla `CROP_ALIASES`.
 */
export function regfiwebDe(raw: string | null | undefined, catalogo?: readonly CultivoOficialMinimo[] | null): string | null {
  const k = claveBusqueda(raw);
  if (!k) return null;
  const tipo = CURADOS.regfiwebTipo.get(k);
  if (tipo) return tipo;
  const r = resolverCultivoSeguro(raw, catalogo);
  return r.code != null ? cultivoCuradoPorCodigo(r.code)?.regfiweb ?? null : null;
}

export type { CultivoCurado };
